import {useEffect, useRef, useState} from "react";
import {ActionIcon, CloseButton} from "@mantine/core";
import {useTranslation} from "react-i18next";
import IconSearch from "../../../assets/search.svg?react";
import {SearchInput} from "./SearchInput";
import styles from "./checklist.module.css";

type CollapsingSearchProps = {
    searchTerm: string;
    onSearchChange: (term: string) => void;
};

/**
 * Narrow-viewport search affordance for the chip row. Collapsed it's a search
 * icon at the end of the chips; tapping expands it to a full-width input
 * (covering the chips) and autofocuses. It collapses on Escape, or on blur when
 * empty — a non-empty value keeps it expanded so the active search stays visible.
 *
 * `SearchInput` is reused untouched; this component owns only the expand/collapse
 * and "is empty" logic. Clearing remounts `SearchInput` (the only way to reset
 * its internal value without adding a prop) and refocuses it.
 */
export const CollapsingSearch = ({
    searchTerm,
    onSearchChange,
}: CollapsingSearchProps) => {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(searchTerm.length > 0);
    const [remountKey, setRemountKey] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Mirror the latest debounced term so blur can decide whether to collapse.
    const valueRef = useRef(searchTerm);

    useEffect(() => {
        if (expanded) {
            wrapperRef.current?.querySelector("input")?.focus();
        }
    }, [expanded, remountKey]);

    const handleChange = (term: string) => {
        valueRef.current = term;
        onSearchChange(term);
    };

    const handleClear = () => {
        valueRef.current = "";
        onSearchChange("");
        setRemountKey((k) => k + 1); // remount SearchInput so its input empties
    };

    if (!expanded) {
        return (
            <ActionIcon
                variant="default"
                size="lg"
                onClick={() => setExpanded(true)}
                aria-label={t("common.search")}
                title={t("common.search")}
                className={styles.searchToggle}
            >
                <IconSearch width={16} height={16} />
            </ActionIcon>
        );
    }

    return (
        <div
            ref={wrapperRef}
            className={styles.searchExpanded}
            onBlur={(e) => {
                // Collapse only when focus leaves the whole field, and only if empty.
                if (
                    !e.currentTarget.contains(e.relatedTarget as Node) &&
                    valueRef.current.length === 0
                ) {
                    setExpanded(false);
                }
            }}
            onKeyDown={(e) => {
                if (e.key === "Escape") setExpanded(false);
            }}
        >
            <div className={styles.searchField}>
                <SearchInput
                    key={remountKey}
                    initialValue={searchTerm}
                    onChange={handleChange}
                />
            </div>
            <CloseButton
                aria-label={t("common.clear", {defaultValue: "Clear"})}
                onClick={handleClear}
            />
        </div>
    );
};
